/** Inline due-date display and calendar editor for a work order. */
import { type ReactElement, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { useApplyPlanningCommand } from "@/features/workorders/queries";
import { InlineEditPopover } from "@/shared/components/inline-edit-popover";
import { formatDateTime } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import { Calendar } from "@/shared/ui/calendar";
import { Input, Label } from "@/shared/ui/form";

/** Display the due date and offer calendar editing to authorized work-order editors. */
export function WorkOrderDueDate({
  workOrderId,
  status,
  dueAt,
  isOverdue,
}: {
  workOrderId: string;
  status: string;
  dueAt: string | null;
  isOverdue: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const update = useApplyPlanningCommand(workOrderId);
  const timeId = useId();
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState("17:00");
  const editable =
    !["closed", "cancelled"].includes(status) &&
    (can("workorders.workorder.edit") || can("workorders.workorder.update_assigned"));

  const changeOpen = (open: boolean): void => {
    if (update.isPending) return;
    if (open) {
      const current = dueAt ? new Date(dueAt) : undefined;
      setDate(current);
      setTime(
        current
          ? `${String(current.getHours()).padStart(2, "0")}:${String(current.getMinutes()).padStart(2, "0")}`
          : "17:00",
      );
      update.reset();
    }
    setEditing(open);
  };

  const save = (): void => {
    const value = date ? new Date(date) : null;
    if (value) {
      const [hours = 0, minutes = 0] = time.split(":").map(Number);
      value.setHours(hours, minutes, 0, 0);
    }
    update.mutate(
      { action: "save", due_at: value?.toISOString() ?? null },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className={isOverdue ? "text-destructive" : undefined}>{formatDateTime(dueAt)}</span>
      {editable ? (
        <InlineEditPopover
          label={t("workOrders.editDueDate")}
          open={editing}
          onOpenChange={changeOpen}
          footer={
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={!date || update.isPending}
                onClick={() => setDate(undefined)}
              >
                {t("workOrders.clearDueDate")}
              </Button>
              <div className="ms-auto flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={update.isPending}
                  onClick={() => setEditing(false)}
                >
                  {t("app.cancel")}
                </Button>
                <Button size="sm" disabled={update.isPending || (!!date && !time)} onClick={save}>
                  {update.isPending ? t("app.loading") : t("app.save")}
                </Button>
              </div>
            </div>
          }
        >
          <Calendar
            mode="single"
            selected={date}
            defaultMonth={date}
            onSelect={setDate}
            disabled={update.isPending}
          />
          <div className="flex items-center gap-3 border-t px-4 py-3">
            <Label htmlFor={timeId} className="shrink-0">
              {t("workOrders.dueTime")}
            </Label>
            <Input
              id={timeId}
              name="due-time"
              type="time"
              value={time}
              disabled={!date || update.isPending}
              onChange={(event) => setTime(event.target.value)}
              required={!!date}
            />
          </div>
          {update.isError ? (
            <p role="alert" className="px-4 pb-3 text-sm text-destructive">
              {errorMessage(update.error)}
            </p>
          ) : null}
        </InlineEditPopover>
      ) : null}
    </div>
  );
}
